import { useEffect, useState } from 'react';
import servicesData from '../data/services.json';
import { ServiceApi } from '../api/adminApi';

const normalizeService = (service = {}, index = 0) => ({
  ...service,
  sortOrder: typeof service.sortOrder === 'number' ? service.sortOrder : index
});

const sortServices = (list = []) =>
  [...list].sort((a, b) => {
    const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    return orderDiff !== 0 ? orderDiff : (a.name || '').localeCompare(b.name || '');
  });

const prepareServices = (list = []) => sortServices(list.map((service, index) => normalizeService(service, index)));

const fallbackServices = prepareServices(servicesData.services);

let cachedServices = null;
let inflightRequest = null;

const fetchServices = async () => {
  if (cachedServices) {
    return cachedServices;
  }
  if (inflightRequest) {
    return inflightRequest;
  }
  inflightRequest = ServiceApi.getAll()
    .then((data) => {
      if (Array.isArray(data) && data.length) {
        cachedServices = prepareServices(data);
        return cachedServices;
      }
      cachedServices = fallbackServices;
      return cachedServices;
    })
    .catch(() => {
      cachedServices = fallbackServices;
      return cachedServices;
    })
    .finally(() => {
      inflightRequest = null;
    });
  return inflightRequest;
};

export const useServicesList = () => {
  const [services, setServices] = useState(cachedServices || fallbackServices);
  const [isLoading, setIsLoading] = useState(!cachedServices);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    if (cachedServices) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    fetchServices()
      .then((data) => {
        if (isMounted) {
          setServices(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError('Unable to load services right now. Showing defaults.');
          setServices(fallbackServices);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return { services, isLoading, error };
};
